import React from 'react';
import { ExpandMore } from '@mui/icons-material';
import { ListItemButton, ListItemIcon, ListItemText, useTheme, Box } from '@mui/material';
import { AlertBox } from '../AlertBox';
import { AlertBoxTitle } from '../AlertBox/AlertBoxTitle';
import { Chip } from '../Chip';
import { Typography } from '../Typography';

export interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  count?: number;
  hasDot?: boolean;
  isSelected?: boolean;
  isExpanded?: boolean;
  isSecondary?: boolean;
  hasSubNav?: boolean;
  isSubNav?: boolean;
  notification?: { text: string };
  details?: string | string[];
  extraDetails?: string;
  isLastItem?: boolean;
}

export const NavItem: React.FC<React.PropsWithChildren<NavItemProps>> = ({
  icon,
  label,
  onClick,
  count,
  hasDot = false,
  isSelected = false,
  isExpanded = false,
  isSecondary = false,
  hasSubNav = false,
  isSubNav = false,
  notification,
  details,
  extraDetails,
  isLastItem = false,
}: NavItemProps) => {
  const { custom: { spacing, radius } = {}, palette } = useTheme();
  const detailLines = [
    ...(Array.isArray(details) ? details : details ? [details] : []),
    ...(extraDetails ? [extraDetails] : []),
  ];

  return (
    <>
      <ListItemButton
        onClick={onClick}
        selected={isSelected}
        aria-expanded={hasSubNav ? isExpanded : undefined}
        sx={{
          pl: isSubNav ? '24px' : spacing?.lg,
          pr: isSubNav ? '8px' : undefined,
          py: isSubNav ? '6px' : undefined,
          height: isSubNav ? '36px' : undefined,
          borderRadius: radius?.sm,
          backgroundColor: isSelected ? palette.secondary.main : 'transparent',
          '&.MuiListItemButton-root svg *': {
            stroke: isSelected ? palette.primary.main : '',
          },
          my: isSubNav ? 0 : spacing?.['xxs'],
          transition: 'box-shadow 0.2s ease-in-out',
          '&:focus-visible': {
            backgroundColor: 'transparent',
            outline: '2px solid #fff',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: -2, // thickness of the colored ring
              borderRadius: 'inherit',
              boxShadow: `0 0 0 2px ${palette.primary.main}`,
              pointerEvents: 'none',
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: icon ? '36px 4fr' : '1fr',
            gridGap: '4px',
            gridTemplateRows: detailLines.length ? '1fr 1fr' : undefined,
            alignItems: 'center',
            width: '100%',
          }}
        >
          {hasDot ? (
            <ListItemIcon sx={{ minWidth: '10px', mr: spacing?.xl }}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
                data-testid="CircleIcon"
              >
                <circle cx="5" cy="5" r="4" fill={palette.primary.main} />
              </svg>
            </ListItemIcon>
          ) : null}
          <ListItemIcon
            sx={{
              minWidth: isSubNav ? '24px' : '24px',
              mr: spacing?.xl,
              '& svg': {
                width: isSubNav ? '24px' : undefined,
                height: isSubNav ? '24px' : undefined,
              },
            }}
          >
            {icon}
          </ListItemIcon>
          <ListItemText sx={{ mb: detailLines.length ? 0 : 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography
                sx={{
                  color: isSelected ? palette.primary.main : palette.text.secondary,
                  fontSize: isSubNav ? '14px' : isSecondary ? '14px' : '16px',
                  fontWeight: isSubNav ? '600' : '600',
                }}
              >
                {label}
              </Typography>
              {count !== undefined && count !== null && <Chip label={count} size="small" />}
              {hasSubNav ? (
                <ExpandMore
                  sx={{
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease-in-out',
                  }}
                />
              ) : null}
            </Box>
          </ListItemText>
          {detailLines.map((line, index) => (
            <ListItemText
              key={`${line}-${index}`}
              sx={{ gridColumn: '2/3', mt: 0 }}
              data-testid={
                index === 0 ? 'nav-item-details' : index === 1 ? 'nav-item-extra-details' : `nav-item-details-${index}`
              }
            >
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  color: 'text.secondary',
                }}
              >
                {line}
              </Typography>
            </ListItemText>
          ))}
        </Box>
      </ListItemButton>
      {notification && (
        <AlertBox
          alertType="error"
          data-testid="nav-item-notification"
          alertBoxMargin={isLastItem ? '8px 0 0 0' : '8px 0 8px 0'}
        >
          <AlertBoxTitle>{notification.text}</AlertBoxTitle>
        </AlertBox>
      )}
    </>
  );
};
